import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Stepper, Dialog } from "antd-mobile";
import { toastSuccess } from "@/utils/toast";
import { getAMapKey, getAMapSafeKey } from "@/utils/location";
import { getStaffDetail, updateTakeAddress } from "@/api/staff";

// ── 类型定义 ──
interface StaffDetailData {
  place: string;
  latitude: string;
  longitude: string;
  radius: number;
}

const BRAND = "#FF6B35";
const MAXMAPLEVEL = 20;

export default function EditPlacePage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [staffInfo, setStaffInfo] = useState<StaffDetailData>({
    place: "",
    latitude: "",
    longitude: "",
    radius: 5,
  });

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<any>(null);
  const scaleRef = useRef(15);
  const radiusRef = useRef(5); // 用 ref 同步最新半径，确保提交时能读到最新值
  const dataVersionRef = useRef(0); // 数据版本号，用于解决异步竞态（API返回时若版本已过期则丢弃）
  const [mapLoaded, setMapLoaded] = useState(false);

  // ── 加载达人信息（对应 uni-app: getStaffDetail） ──
  const loadStaffInfo = useCallback(async () => {
    const versionAtCallTime = dataVersionRef.current; // 记录调用时的版本号
    try {
      const info = await getStaffDetail();
      // 版本号检查：如果期间用户已选择过新地址（版本号已递增），则丢弃旧数据
      if (dataVersionRef.current !== versionAtCallTime) return;
      const radius = info.service_radius ?? 5;
      radiusRef.current = radius; // 同步到 ref
      setStaffInfo({
        place: info.place || "",
        latitude: String(info.latitude ?? ""),
        longitude: String(info.longitude ?? ""),
        radius,
      });
    } catch {
      // 错误已在 request 层统一 toast
    }
  }, []);

  // 首次加载
  useEffect(() => {
    loadStaffInfo();
  }, [loadStaffInfo]);

  // ── 初始化高德地图（依赖 staffInfo 数据） ──
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current || !staffInfo.latitude)
      return;

    if ((window as any).AMap) {
      initMap();
      return;
    }

    try {
      const key = getAMapKey();
      const safeKey = getAMapSafeKey();

      if (safeKey) {
        (window as any)._AMapSecurityConfig = { securityJsCode: safeKey };
      }

      const script = document.createElement("script");
      script.src = `https://webapi.amap.com/maps?v=2.0&key=${key}&plugin=AMap.Geocoder`;
      script.onload = () => {
        initMap();
      };
      script.onerror = () => console.error("高德地图 SDK 加载失败");
      document.head.appendChild(script);
    } catch (err) {
      console.error("地图初始化失败:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffInfo.latitude]);

  function initMap() {
    const AMap = (window as any).AMap;
    if (!AMap || !mapRef.current) return;

    const lat = parseFloat(staffInfo.latitude) || 18.2528;
    const lng = parseFloat(staffInfo.longitude) || 109.5123;
    const radius = staffInfo.radius * 1000; // km → m

    const map = new AMap.Map(mapRef.current, {
      zoom: scaleRef.current,
      center: [lng, lat],
      showIndoorMap: false,
    });

    mapInstanceRef.current = map;

    // 标记点
    const marker = new AMap.Marker({
      position: [lng, lat],
      title: "当前位置",
      draggable: true,
    });
    marker.setMap(map);
    markerRef.current = marker;

    // 服务范围圆圈
    if (radius > 0) {
      const circle = new AMap.Circle({
        center: [lng, lat],
        radius,
        fillColor: "#FF6B351A",
        fillOpacity: 0.3,
        strokeColor: BRAND,
        strokeWeight: 2,
        strokeStyle: "solid",
        strokeOpacity: 0.8,
      });
      circle.setMap(map);
      circleRef.current = circle;
    }

    map.on("click", (e: any) => handleMapClick(e.lnglat));
    setMapLoaded(true);

    // 标尺控件
    AMap.plugin(["AMap.Scale"], () => {
      map.addControl(new AMap.Scale());
    });
  }

  // ── 地图点击选点 ──
  function handleMapClick(lnglat: any) {
    const { lng, lat } = lnglat;

    setStaffInfo((prev) => ({
      ...prev,
      latitude: String(lat),
      longitude: String(lng),
    }));

    if (markerRef.current) {
      markerRef.current.setPosition([lng, lat]);
    }
    if (circleRef.current) {
      circleRef.current.setCenter([lng, lat]);
    }

    // 逆地理编码获取地址名
    const AMap = (window as any).AMap;
    if (AMap) {
      const geocoder = new AMap.Geocoder();
      geocoder.getAddress([lng, lat], (status: string, result: any) => {
        if (status === "complete" && result.regeocode) {
          setStaffInfo((prev) => ({
            ...prev,
            place: result.regeocode.formattedAddress,
          }));
        }
      });
    }
  }

  // ── 地图缩放控制 ──
  function zoomIn() {
    const map = mapInstanceRef.current;
    if (!map) return;
    let z = Math.min(map.getZoom() + 1, MAXMAPLEVEL);
    map.setZoom(z);
    scaleRef.current = z;
  }

  function zoomOut() {
    const map = mapInstanceRef.current;
    if (!map) return;
    let z = Math.max(map.getZoom() - 1, 3);
    map.setZoom(z);
    scaleRef.current = z;
  }

  // ── 半径步进器 ──
  function changeRadius(val: number | null) {
    if (!val || val < 1) return;
    radiusRef.current = val; // 同步到 ref
    setStaffInfo((prev) => ({ ...prev, radius: val }));
    if (circleRef.current && mapInstanceRef.current) {
      circleRef.current.setRadius(val * 1000);
    }
  }

  // ── 跳转选点页面 ──
  function goSelectPlace() {
    navigate("/location-select", { state: { from: "/workspace/edit-place" } });
  }

  // ── 检查 location-select 返回的数据 ──
  useEffect(() => {
    const state = location.state as any;
    if (state?.selectedLat && state?.selectedLng) {
      // 递增版本号，使正在进行的 loadStaffInfo 异步结果失效
      dataVersionRef.current++;

      const lat = Number(state.selectedLat);
      const lng = Number(state.selectedLng);

      setStaffInfo((prev) => ({
        ...prev,
        place: state.selectedCity || `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
        latitude: String(lat),
        longitude: String(lng),
      }));

      if (mapInstanceRef.current) {
        if (markerRef.current) markerRef.current.setPosition([lng, lat]);
        if (circleRef.current) circleRef.current.setCenter([lng, lat]);
        mapInstanceRef.current.setZoomAndCenter(14, [lng, lat], false, 300);
      }
    }
  }, [location.state]);

  // ── 提交保存（对应 uni-app: updateStaffLocationAndRange） ──
  function handleSubmit() {
    if (!staffInfo.place || !staffInfo.latitude || !staffInfo.longitude) {
      toastSuccess("请选择接单位置");
      return;
    }

    Dialog.show({
      content: "是否确认该接单地址？",
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "取消",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "确认保存",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              try {
                await updateTakeAddress({
                  latitude: staffInfo.latitude,
                  longitude: staffInfo.longitude,
                  place: staffInfo.place,
                  service_radius: radiusRef.current, // 从 ref 读取最新值
                });
                toastSuccess("修改成功");
                setTimeout(() => navigate(-1), 1000);
              } catch {
                // 错误已在 request 层统一 toast
              }
            },
          },
        ],
      ],
    });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header — 品牌橙色 */}
      <div style={{ background: BRAND }}>
        <div
          className="px-5 flex items-center gap-3"
          style={{
            paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)",
            paddingBottom: 13,
          }}
        >
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] shrink-0"
            style={{ background: "rgba(255,255,255,0.2)" }}
          >
            <i
              className="ri-arrow-left-line"
              style={{ fontSize: 20, color: "#fff" }}
            />
          </button>
          <span className="text-lg font-bold text-white">接单位置</span>
        </div>
      </div>

      {/* 内容区域 — 对应 Vue: pageEditPlace/index.vue */}
      <div className="px-4 pt-4 space-y-3">
        {/* 主卡片（地址选择 + 服务半径） */}
        <div className="bg-white rounded-2xl overflow-hidden">
          {/* 标题栏：橙色竖条 + 标题 + 提示 */}
          <div className="flex items-center px-4 py-3.5 relative">
            <div
              className="absolute left-0 top-3 w-[5px] h-[22px] rounded-r-full"
              style={{ background: "#FE7A3A" }}
            />
            <span className="text-base font-bold text-gray-900 ml-2">
              接单位置
            </span>
            <span className="text-xs ml-1.5" style={{ color: "#FF2525" }}>
              (用于计算往返距离)
            </span>
          </div>
          <div className="flex flex-col px-3">
            {/* 地址选择 */}
            <button
              type="button"
              onClick={goSelectPlace}
              className="flex items-center gap-2 mb-3 bg-[#F8F8F8] rounded-xl px-4 py-3 active:bg-gray-100 transition-colors w-full text-left overflow-hidden"
            >
              <i
                className="ri-map-pin-line text-lg shrink-0"
                style={{ color: BRAND }}
              />
              <span
                className="text-sm flex-1 min-w-0"
                style={{
                  color: staffInfo.place ? "#333" : "#999",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {staffInfo.place || "请选择地址"}
              </span>
            </button>

            {/* 服务半径 */}
            <div className="flex items-center gap-2 mb-3 bg-[#F8F8F8] rounded-xl px-4 py-3">
              <i
                className="ri-radius-setting text-lg shrink-0"
                style={{ color: BRAND }}
              />
              <span className="flex-1 text-sm text-gray-700">服务半径</span>
              <Stepper
                value={staffInfo.radius}
                onChange={(val) => changeRadius(val)}
                min={1}
                step={1}
                style={
                  {
                    "--color": BRAND,
                    "--border-color": "#E5E7EB",
                  } as React.CSSProperties
                }
              />
            </div>
          </div>
        </div>

        {/* 地图区域 */}
        <div
          className="mt-3 mx-4 rounded-xl overflow-hidden shadow-sm relative"
          style={{ height: 280 }}
        >
          {!mapLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
              <div className="text-center space-y-2">
                <i className="ri-loader-4-line text-2xl animate-spin text-gray-400 block mx-auto" />
                <p className="text-xs text-gray-400">地图加载中...</p>
              </div>
            </div>
          )}
          <div ref={mapRef} className="w-full h-full" />

          {/* 缩放按钮 */}
          <div className="absolute right-3 bottom-3 flex flex-col gap-1 z-[430]">
            <button
              type="button"
              onClick={zoomIn}
              className="w-8 h-8 bg-white/95 rounded-lg shadow flex items-center justify-center active:bg-gray-50 cursor-pointer"
            >
              <i className="ri-add-line text-base text-gray-700" />
            </button>
            <button
              type="button"
              onClick={zoomOut}
              className="w-8 h-8 bg-white/95 rounded-lg shadow flex items-center justify-center active:bg-gray-50 cursor-pointer"
            >
              <i className="ri-subtract-line text-base text-gray-700" />
            </button>
          </div>

          {/* 提示文字 */}
          <div className="absolute left-3 top-3 z-[430] px-2.5 py-1.5 rounded-md bg-black/40 backdrop-blur-sm">
            <p className="text-[11px] leading-relaxed text-white/90">
              点击地图选择服务位置，拖动标记微调
            </p>
          </div>
        </div>
      </div>

      {/* 底部操作栏 */}
      <div className="fixed bottom-12 left-0 right-0 z-40 px-4 pb-3 pt-2 bg-gradient-to-t from-gray-50 via-gray-50/95 to-transparent">
        <button
          type="button"
          onClick={handleSubmit}
          className="w-full py-3 rounded-2xl text-white text-base font-semibold active:opacity-90 transition-opacity cursor-pointer shadow-lg"
          style={{ background: `linear-gradient(135deg, ${BRAND}, #ff8c5a)` }}
        >
          保存修改
        </button>
      </div>
    </div>
  );
}
